/**
 * "Create an interface" on the Interface tab (task 1131).
 *
 * Both of the tab's entry points — the empty state and the Public interface
 * section — render this one component, so they send the classifier one
 * phrasing. The click IS the request (task 1201): through the shared
 * `useAssistantHandoff` hook it opens the Assistant, sends
 * `CREATE_INTERFACE_PROMPT` as the User's own first turn of a NEW thread,
 * pulses the composer, and holds a sent state for about a second (check icon,
 * "Sent to Assistant", disabled), exactly as Suggest criteria and Generate
 * tests do. The UI Composer asks in the thread what the interface should show
 * when the request is thin, so nothing needs describing up front.
 *
 * The Assistant is the only way to create an interface since the standalone UI
 * Composer page was retired (task 1132). With `innovate.fe.interface.assistant-entry`
 * off, or no Assistant provider mounted (the sidebar flags are off), there is
 * nothing for the button to open, so it renders nothing rather than a control
 * that cannot act.
 */
import { CheckIcon } from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useAssistantHandoff } from '@/hooks/useAssistantHandoff';

/** The one prompt both entry points send. */
export const CREATE_INTERFACE_PROMPT = 'Create an interface for this AI Service';

interface CreateInterfaceButtonProps {
  className: string;
  testId?: string;
}

export function CreateInterfaceButton({ className, testId }: CreateInterfaceButtonProps) {
  const assistantEntry = useFlag('innovate.fe.interface.assistant-entry');
  // Off, the click places the prompt in the composer unsent (task 1215).
  const autoSubmit = useFlag('innovate.fe.testing.assistant-handoff');
  const { assistant, sent, handoff } = useAssistantHandoff(CREATE_INTERFACE_PROMPT, { autoSubmit });

  if (!assistantEntry || !assistant) {
    return null;
  }

  return (
    <button
      type="button"
      data-testid={testId}
      data-sent={sent ? 'true' : undefined}
      onClick={handoff}
      disabled={sent}
      className={`${className} transition-colors motion-reduce:transition-none`}
    >
      {sent && (
        <CheckIcon
          data-testid={testId ? `${testId}-sent-icon` : undefined}
          className="h-4 w-4 mr-2 transition-opacity motion-reduce:transition-none"
          aria-hidden="true"
        />
      )}
      {sent ? 'Sent to Assistant' : 'Create an interface'}
    </button>
  );
}

export default CreateInterfaceButton;
